import { Dispatch, SetStateAction, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert, Divider, Stack, Typography } from '@mui/material';
import { MembershipRole } from 'tdc-web-backend/enums/enums';
import { CreateUserInvitationDto } from 'tdc-web-backend/user-invitation/schemas';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import CustomInput from '../../../../../../components/custom-input/CustomInput';
import ConfirmModal from '../../../../../../components/confirm-modal/ConfirmModal';
import useCreate from '../../../../../../utils/hooks/crud-hooks/useCreate';
import { ReactComponent as ClipboardCheckIcon } from '../../../../../../assets/icons/layout-icons/ClipboardCheckIconEditModal.svg';
import { primaryLight } from '../../../../../../utils/color';
import MembershipRoles from './MembershipRoles';

export type AddModalData = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  role: MembershipRole;
};

type AddModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSuccessfulSubmit?: () => void;
  data?: AddModalData;
};

const AddModal = ({ isOpen, setIsOpen, onSuccessfulSubmit }: AddModalProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      membershipRole: MembershipRole.Editor,
    },
  });

  const { mutate, isLoading } = useCreate<{}, CreateUserInvitationDto>({
    resource: 'user-invitation',
  });

  const onSubmit = () => {
    mutate(
      {
        email: form.getValues('email'),
        firstName: form.getValues('firstName'),
        lastName: form.getValues('lastName'),
        jobTitle: form.getValues('jobTitle'),
        role: form.getValues('membershipRole'),
      },
      {
        onSuccess: () => {
          setError(null);
          form.reset();
          setIsOpen(false);
          onSuccessfulSubmit?.();
        },
        onError: (err) => {
          setError(err.response?.data.message || err.message);
        },
      },
    );
  };

  return (
    <ConfirmModal
      isModalOpen={isOpen}
      automaticallyCloseModalOnButtonClick={false}
      onCancel={() => setIsOpen(false)}
      onConfirm={form.handleSubmit(onSubmit)}
      setIsModalOpen={() => null}
      title={t('settings.members.inviteNewMember')}
      width="900px"
      confirmButtonText={t('settings.members.sendInvitation')}
      iconProps={{
        icon: <ClipboardCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: '#E3DBFC',
      }}
      isLoading={isLoading}
      showCancelButton={!isLoading}
      message={
        <FormProvider {...form}>
          <form>
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Stack
                direction="row"
                spacing={3}
                justifyContent="cener"
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Stack width="50%" spacing={3} justifyContent="space-between">
                  <CustomInput
                    input={<CustomInput.Text />}
                    name="email"
                    type="email"
                    label={`* ${t('common.email')}`}
                    placeholder={`${t('common.enter')} ${t('common.email')}`}
                    required
                    disabled={isLoading}
                    validationRegEx={{
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t('errors.invalidEmailAddress'),
                    }}
                  />
                  <CustomInput
                    input={<CustomInput.Text />}
                    placeholder={`${t('common.enter')} ${t('common.jobTitle')}`}
                    name="jobTitle"
                    label={t('common.jobTitle')}
                    disabled={isLoading}
                  />
                </Stack>
                <Stack width="50%" spacing={3} justifyContent="space-between">
                  <CustomInput
                    input={<CustomInput.Text />}
                    placeholder={`${t('common.enter')} ${t('common.firstName')}`}
                    name="firstName"
                    label={t('common.firstName')}
                    disabled={isLoading}
                  />
                  <CustomInput
                    input={<CustomInput.Text />}
                    placeholder={`${t('common.enter')} ${t('common.lastName')}`}
                    name="lastName"
                    label={t('common.lastName')}
                    disabled={isLoading}
                  />
                </Stack>
              </Stack>
              <Divider />
              <MembershipRoles disabled={isLoading} />
            </Stack>
          </form>
        </FormProvider>
      }
    />
  );
};

export default AddModal;
